import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Container,
  Form,
  Modal,
  Table,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router";
import { toast } from "react-toastify";

export default function GatewayConfigs() {
  const navigate = useNavigate();
  const location = useLocation();

  const [configs, setConfigs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showNumbersModal, setShowNumbersModal] = useState(false);
  const [numbersGatewayId, setNumbersGatewayId] = useState(null);
  const [numbersGatewayName, setNumbersGatewayName] = useState("");
  const [gatewayNumbers, setGatewayNumbers] = useState([]);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  function emptyForm() {
    return {
      gateway_name: "",
      db_ref: "",
      db_dsn: "",
      send_api_url: "",
      send_api_username: "",
      send_api_password: "",
      dynamic_header: "",
      test_mobile_number: "",
      two_way_enabled: false,
      env_token: "",
      active: true,
    };
  }

  const fetchConfigs = async () => {
    const resp = await fetch("/auth/gateway-configs");
    const data = await resp.json();
    if (resp.ok) {
      setConfigs(data.data || []);
    } else {
      toast.error(data.error || "Failed to load gateway configs");
      if (resp.status === 401) navigate("/login", { state: { from: location } });
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (cfg) => {
    setEditId(cfg.id);
    setForm({
      gateway_name: cfg.gateway_name,
      db_ref: cfg.db_ref || "",
      db_dsn: "",
      send_api_url: cfg.send_api_url,
      send_api_username: cfg.send_api_username,
      send_api_password: "",
      dynamic_header: cfg.dynamic_header || "",
      test_mobile_number: cfg.test_mobile_number || "",
      two_way_enabled: cfg.two_way_enabled || false,
      env_token: cfg.env_token || "",
      active: cfg.active,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    const method = editId ? "PATCH" : "POST";
    const url = editId
      ? `/auth/gateway-configs/${editId}`
      : "/auth/gateway-configs";

    const body = { ...form };
    // don't send empty password on update
    if (editId && body.send_api_password === "") {
      delete body.send_api_password;
    }

    const resp = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await resp.json();
    if (resp.ok) {
      toast.success(data.message);
      setShowModal(false);
      fetchConfigs();
    } else {
      toast.error(data.error || "Save failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this gateway config?")) return;
    const resp = await fetch(`/auth/gateway-configs/${id}`, {
      method: "DELETE",
    });
    const data = await resp.json();
    if (resp.ok) {
      toast.success(data.message);
      fetchConfigs();
    } else {
      toast.error(data.error || "Delete failed");
    }
  };

  const handleRefreshNumbers = async (id, name) => {
    toast.info(`Refreshing numbers for ${name}…`);
    const resp = await fetch(`/auth/gateway-configs/${id}/refresh-numbers`, {
      method: "POST",
    });
    const data = await resp.json();
    if (resp.ok) {
      toast.success(`${data.total} numbers synced (${data.rows_affected} affected)`);
    } else {
      toast.error(data.error || "Refresh failed");
    }
  };

  const handlePullDRs = async (id, name) => {
    toast.info(`Pulling DLRs for ${name}...`);
    const resp = await fetch(`/auth/gateway-configs/${id}/pull-drs`, {
      method: "POST",
    });
    const data = await resp.json();
    if (resp.ok) {
      toast.success(`DLRs pulled: ${data.applied}/${data.entries} applied (${data.pages_fetched} page(s))`);
      return;
    }
    toast.error(data.error || "Pull DLRs failed");
  };

  const openGatewayNumbers = async (id, name) => {
    const resp = await fetch(`/auth/monitored-numbers?gateway_config_id=${id}&limit=2000`);
    const data = await resp.json();
    if (resp.ok) {
      setNumbersGatewayId(id);
      setNumbersGatewayName(name);
      setGatewayNumbers(data.data || []);
      setShowNumbersModal(true);
      return;
    }
    toast.error(data.error || "Failed to load monitored numbers");
  };

  const toggleCronForNumber = async (number) => {
    const resp = await fetch(`/auth/monitored-numbers/${number.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ manual_selected: !number.manual_selected }),
    });
    const data = await resp.json();
    if (!resp.ok) {
      toast.error(data.error || "Failed to update cron toggle");
      return;
    }

    toast.success(number.manual_selected ? "Enabled in cron" : "Disabled from cron");

    if (numbersGatewayId) {
      openGatewayNumbers(numbersGatewayId, numbersGatewayName);
    }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Container fluid>
      <div className="d-flex align-items-center my-3 gap-3">
        <h3 className="mb-0">Gateway Configs</h3>
        <Button size="sm" onClick={openCreate}>
          + Add Gateway
        </Button>
      </div>

      <Table bordered hover responsive>
        <thead>
          <tr>
            <th>Name</th>
            <th>DB Ref</th>
            <th>Send API URL</th>
            <th>Dynamic Header</th>
            <th>VN Test Mobile</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {configs.map((cfg) => (
            <tr key={cfg.id}>
              <td>{cfg.gateway_name}</td>
              <td>
                <code>{cfg.db_ref || "—"}</code>
                {cfg.has_db_dsn && (
                  <Badge bg="info" className="ms-1">
                    custom DSN
                  </Badge>
                )}
              </td>
              <td>
                <code style={{ wordBreak: "break-all" }}>{cfg.send_api_url}</code>
              </td>
              <td>{cfg.dynamic_header || "—"}</td>
              <td>
                {cfg.test_mobile_number
                  ? <code>{cfg.test_mobile_number}</code>
                  : <span className="text-muted small">not set</span>}
              </td>
              <td>
                <Badge bg={cfg.active ? "success" : "secondary"}>
                  {cfg.active ? "active" : "inactive"}
                </Badge>
              </td>
              <td>
                <div className="d-flex gap-1 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => openEdit(cfg)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-success"
                    onClick={() => handleRefreshNumbers(cfg.id, cfg.gateway_name)}
                  >
                    Refresh Numbers
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-info"
                    onClick={() => openGatewayNumbers(cfg.id, cfg.gateway_name)}
                  >
                    View Numbers
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-warning"
                    onClick={() => handlePullDRs(cfg.id, cfg.gateway_name)}
                  >
                    Pull DLRs
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleDelete(cfg.id)}
                  >
                    Delete
                  </Button>
                </div>
              </td>
            </tr>
          ))}
          {configs.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center text-muted">
                No gateway configs. Add one to start monitoring inbound delivery.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Create / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editId ? "Edit Gateway Config" : "Add Gateway Config"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Gateway Name *</Form.Label>
              <Form.Control
                value={form.gateway_name}
                onChange={(e) => setField("gateway_name", e.target.value)}
                placeholder="e.g. Production Gateway"
              />
            </Form.Group>

            <div className="alert alert-info py-2">
              Provide either a <strong>DB Ref</strong> (matching an env-configured
              DB) or a <strong>DB DSN</strong> (for standalone connections). DB Ref
              takes precedence when both are set.
            </div>

            <Form.Group className="mb-3">
              <Form.Label>DB Ref</Form.Label>
              <Form.Control
                value={form.db_ref}
                onChange={(e) => setField("db_ref", e.target.value)}
                placeholder="e.g. gw1 (must match DB_<n>_ID env var)"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>DB DSN {editId && "(leave blank to keep existing)"}</Form.Label>
              <Form.Control
                value={form.db_dsn}
                onChange={(e) => setField("db_dsn", e.target.value)}
                placeholder="user:pass@tcp(host:3306)/dbname?parseTime=True"
                type="password"
              />
              <Form.Text className="text-muted">
                Stored encrypted at rest.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Send API URL *</Form.Label>
              <Form.Control
                value={form.send_api_url}
                onChange={(e) => setField("send_api_url", e.target.value)}
                placeholder="https://direct-api.example.com/v1/Sms1"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>API Username *</Form.Label>
              <Form.Control
                value={form.send_api_username}
                onChange={(e) => setField("send_api_username", e.target.value)}
                placeholder="API username"
              />
              <Form.Text className="text-muted">Stored encrypted at rest.</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                API Password{" "}
                {editId && (
                  <span className="text-muted">(leave blank to keep existing)</span>
                )}{" "}
                *
              </Form.Label>
              <Form.Control
                type="password"
                value={form.send_api_password}
                onChange={(e) => setField("send_api_password", e.target.value)}
                placeholder={editId ? "••••••••" : "API password"}
              />
              <Form.Text className="text-muted">Stored encrypted at rest.</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Dynamic Header</Form.Label>
              <Form.Control
                value={form.dynamic_header}
                onChange={(e) => setField("dynamic_header", e.target.value)}
                placeholder="e.g. BoomTest"
              />
              <Form.Text className="text-muted">
                Text sender ID used in outbound test messages to INs.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>VN Test Mobile Number</Form.Label>
              <Form.Control
                value={form.test_mobile_number}
                onChange={(e) => setField("test_mobile_number", e.target.value)}
                placeholder="e.g. 447700900123"
              />
              <Form.Text className="text-muted">
                Required for VN testing. The test message is sent <strong>from</strong> the
                VN <strong>to</strong> this number; the ACK fires when the reply arrives back
                at the VN. Not used for IN testing.
              </Form.Text>
            </Form.Group>

            <Form.Check
              type="switch"
              id="two-way-switch"
              className="mb-3"
              label="Enable 2-way (Sms2) for VN numbers"
              checked={form.two_way_enabled}
              disabled={!form.test_mobile_number}
              onChange={(e) => setField("two_way_enabled", e.target.checked)}
            />
            {!form.test_mobile_number && (
              <p className="text-muted small mt-n2 mb-3">
                Set a VN Test Mobile Number above to enable 2-way mode.
              </p>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Env Token Override</Form.Label>
              <Form.Control
                value={form.env_token}
                onChange={(e) => setField("env_token", e.target.value)}
                placeholder="Leave blank to use global VN_TEST_TOKEN"
              />
            </Form.Group>

            <Form.Check
              type="switch"
              label="Active"
              checked={form.active}
              onChange={(e) => setField("active", e.target.checked)}
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {editId ? "Save Changes" : "Create"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showNumbersModal} onHide={() => setShowNumbersModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Fetched Numbers - {numbersGatewayName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>Number</th>
                <th>Type</th>
                <th>Supplier ID</th>
                <th>Supplier Name</th>
                <th>Cron</th>
                <th>Active</th>
                <th>Last Refreshed</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {gatewayNumbers.map((n) => (
                <tr key={n.id}>
                  <td><code>{n.number}</code></td>
                  <td>{n.number_type}</td>
                  <td>{n.supplier_id}</td>
                  <td>{n.supplier_name || "—"}</td>
                  <td>
                    <Badge bg={n.manual_selected ? "secondary" : "success"}>
                      {n.manual_selected ? "excluded" : "included"}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg={n.active ? "success" : "secondary"}>
                      {n.active ? "active" : "inactive"}
                    </Badge>
                  </td>
                  <td>{n.last_refreshed_at || "—"}</td>
                  <td>
                    <Button
                      size="sm"
                      variant={n.manual_selected ? "outline-success" : "outline-secondary"}
                      onClick={() => toggleCronForNumber(n)}
                    >
                      {n.manual_selected ? "Enable Cron" : "Disable Cron"}
                    </Button>
                  </td>
                </tr>
              ))}
              {gatewayNumbers.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted">No numbers found for this gateway.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>
    </Container>
  );
}
